// Driving controllers shared by the test bots, the physics tests and the benchmark autopilot.
// Pure TS (no three.js); they drive the real physics through ControlInput like a player would.
import type { Vehicle } from '../sim/vehicle';
import { DEG, clamp } from '../sim/math';

export interface PathSample {
  x: number;
  z: number;
}

/** A path the controllers can follow: closest-point query + point at arc length. */
export interface FollowPath {
  /** Arc length of the closest point to (x, z), searching near `hint` if given. */
  project(x: number, z: number, hint?: number): number;
  pointAt(s: number, out: PathSample): PathSample;
  /** Signed lateral offset of (x, z) from the path at s (+ = left). */
  lateral(x: number, z: number, s: number): number;
  length: number;
  closed: boolean;
}

/** Circle path (skidpad), counter-clockwise when seen from above = turning left when driven +s. */
export function circlePath(cx: number, cz: number, r: number, left = true): FollowPath {
  const L = 2 * Math.PI * r;
  // Parametrise so increasing s moves counter-clockwise (left turn) or clockwise.
  const dir = left ? 1 : -1;
  const angOf = (x: number, z: number) => Math.atan2(x - cx, z - cz);
  return {
    length: L,
    closed: true,
    project(x, z) {
      let a = angOf(x, z) * dir;
      if (a < 0) a += Math.PI * 2;
      return (a / (Math.PI * 2)) * L;
    },
    pointAt(s, out) {
      const a = ((s / L) * Math.PI * 2) * dir;
      out.x = cx + Math.sin(a) * r;
      out.z = cz + Math.cos(a) * r;
      return out;
    },
    lateral(x, z) {
      const d = Math.hypot(x - cx, z - cz);
      // Left of a CCW path is toward the centre.
      return left ? r - d : d - r;
    },
  };
}

/** Straight line path along heading from (x0, z0). */
export function linePath(x0: number, z0: number, heading: number, length: number): FollowPath {
  const dx = Math.sin(heading);
  const dz = Math.cos(heading);
  return {
    length,
    closed: false,
    project(x, z) {
      return clamp((x - x0) * dx + (z - z0) * dz, 0, length);
    },
    pointAt(s, out) {
      out.x = x0 + dx * s;
      out.z = z0 + dz * s;
      return out;
    },
    lateral(x, z) {
      // left of heading = +X when heading 0
      return (x - x0) * dz - (z - z0) * dx;
    },
  };
}

const tmp: PathSample = { x: 0, z: 0 };

/**
 * Pure-pursuit steering toward a lookahead point on the path. Returns the steering input
 * (-1 left .. +1 right) and the arc-length position.
 */
export function purePursuit(v: Vehicle, path: FollowPath, sHint: number, lookahead: number, sOut?: { s: number }): number {
  const p = v.pos;
  const s = path.project(p.x, p.z, sHint);
  if (sOut) sOut.s = s;
  let st = s + lookahead;
  if (path.closed) st = ((st % path.length) + path.length) % path.length;
  else st = Math.min(st, path.length);
  path.pointAt(st, tmp);
  // Target in car frame: forward = fwd, left = leftDir.
  const dx = tmp.x - p.x;
  const dz = tmp.z - p.z;
  const fwd = dx * v.fwd.x + dz * v.fwd.z;
  const left = dx * v.leftDir.x + dz * v.leftDir.z;
  const alpha = Math.atan2(left, Math.max(0.1, fwd));
  const wb = Math.abs(v.spec.wheels[0].position[2] - v.spec.wheels[2].position[2]);
  const Ld = Math.hypot(dx, dz);
  const delta = Math.atan2(2 * wb * Math.sin(alpha), Math.max(1, Ld)); // + = left
  const maxSteer = v.spec.steering.maxAngleDeg * DEG;
  return clamp(-delta / maxSteer, -1, 1);
}

/** Simple speed controller: PI on speed error → throttle or brake. */
export class SpeedController {
  private integ = 0;
  kp = 0.6;
  ki = 0.25;
  brakeGain = 0.35;
  /** Overspeed (m/s) tolerated before braking. */
  brakeThreshold = 1.5;
  /** The result object is reused: read it before the next update. */
  private readonly out = { throttle: 0, brake: 0 };
  update(current: number, target: number, dt: number): { throttle: number; brake: number } {
    const e = target - current;
    const o = this.out;
    if (e < -this.brakeThreshold) {
      this.integ = 0;
      o.throttle = 0;
      o.brake = clamp(-e * this.brakeGain, 0, 1);
      return o;
    }
    this.integ = clamp(this.integ + e * dt, -2, 4);
    o.throttle = clamp(e * this.kp + this.integ * this.ki, 0, 1);
    o.brake = 0;
    return o;
  }
  reset(): void {
    this.integ = 0;
  }
}
